import React, { useState } from 'react';
import api from '../api';

const ROLES = {
  superadmin: { label: 'Super Admin', below: [] },
  paas: { label: 'PaaS Admin', below: ['saas', 'tenant', 'user'] },
  saas: { label: 'SaaS Admin', below: ['tenant', 'user'] },
  tenant: { label: 'Client Admin', below: ['user'] },
  user: { label: 'User', below: [] }
};

export default function CreditTransfer({ isOpen, wallet, ownerId, ownerType, onClose, onSuccess }) {
  const [recipientId, setRecipientId] = useState('');
  const [recipientType, setRecipientType] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const availableRecipients = ROLES[ownerType]?.below || [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const numAmount = parseInt(amount);
    if (!numAmount || numAmount <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    if (!recipientId || !recipientType) {
      setError('Please select a recipient');
      return;
    }
    if (wallet && numAmount > (wallet.balance - wallet.reservedBalance)) {
      setError('Insufficient balance');
      return;
    }

    setLoading(true);
    try {
      await api.post('/credits/transfer', {
        fromOwnerId: ownerId,
        fromRole: ownerType,
        toOwnerId: recipientId,
        toRole: recipientType,
        amount: numAmount,
        description: description,
        approvalRequired: false
      });
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Transfer failed');
    } finally {
      setLoading(false);
    }
  };

  const recipientLabels = {
    saas: 'SaaS Admin',
    tenant: 'Client Admin',
    user: 'User'
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex justify-between items-center px-6 py-4 border-b">
          <h3 className="text-lg font-semibold">Transfer Credits</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
              {error}
            </div>
          )}

          <div className="bg-gray-50 rounded p-3 text-sm">
            <p>Available Balance: <span className="font-bold">{wallet?.availableBalance?.toLocaleString() || 0}</span></p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Recipient Role</label>
            <select
              value={recipientType}
              onChange={(e) => {
                setRecipientType(e.target.value);
                setRecipientId('');
              }}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Select role</option>
              {availableRecipients.map(r => (
                <option key={r} value={r}>{recipientLabels[r] || r}</option>
              ))}
            </select>
          </div>

          {recipientType && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Recipient ID</label>
              <input
                type="text"
                value={recipientId}
                onChange={(e) => setRecipientId(e.target.value)}
                placeholder="Enter recipient ID"
                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <p className="text-xs text-gray-400 mt-1">Enter the ID of the {recipientLabels[recipientType] || recipientType}</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              min="1"
              max={wallet?.availableBalance}
              placeholder="Enter amount"
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Add a note..."
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div className="flex space-x-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border text-gray-700 rounded hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-primary-500 text-white rounded hover:bg-primary-600 disabled:opacity-50"
            >
              {loading ? 'Transferring...' : 'Transfer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}